import * as XLSX from 'xlsx';
import {
  Account,
  CustomerCategory,
  LoanType,
  User,
  AccountAllocation,
  AllocationHistory,
  FollowUp,
  PTPRecord,
  RecoveryRecord,
  FieldVisit,
  PhotoRecord,
  VoiceNoteRecord,
  DocumentRecord,
  ActivityLog,
  CommissionRecord,
} from '../types';
import { productOf } from './collections';

import { todayLocal, toLocalDateString, sheetDateToString } from './dates';
export interface ParsedExcelRow {
  rowNumber: number;
  raw: Record<string, any>;
  zone?: string;
  area?: string;
  city?: string;
  coordinates?: string;
  latitude?: number;
  longitude?: number;
  branchCode: string;
  branchName: string;
  accountNo: string;
  customerName: string;
  productDesc: string;
  facility: string;
  limitSanctioned: number;
  loanBalance: number;
  customerBalance?: number;
  npaDate: string;
  contactNo: string;
  customerId: string;
  address: string;
  assignedAgentId?: string;
  assignedAgentName?: string;
  riskClassification?: string;
  accountCategory?: string;
  latestRemark?: string;
  isValid: boolean;
  validationErrors: string[];
  // Multi-account detection
  isMultipleAccount?: boolean;
  multipleAccountsCount?: number;
  linkedAccountNumbers?: string[];
  multipleAccountReason?: string;
  multipleAccountGroupKey?: string;
}

export interface ExcelImportResult {
  fileName: string;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  multipleAccountsCount: number;
  multipleCustomersCount: number;
  rows: ParsedExcelRow[];
  detectedColumns: string[];
}

// Clean and normalize column key
function normalizeKey(key: string): string {
  return key
    .toUpperCase()
    .trim()
    .replace(/[^A-Z0-9]/g, '_');
}

/**
 * Normalizes an account number or loan number for clean deduplication / matching
 */
export function normalizeAccountKey(accNo?: string | number): string {
  if (!accNo) return '';
  return String(accNo)
    .toUpperCase()
    .trim()
    .replace(/^ACC-/, '')
    .replace(/^LN-/, '')
    .replace(/[^A-Z0-9]/g, '');
}

/**
 * Checks if two account identifiers represent the exact same account
 */
export function isMatchingAccountNumber(acc1?: string | number, acc2?: string | number): boolean {
  if (!acc1 || !acc2) return false;
  const k1 = normalizeAccountKey(acc1);
  const k2 = normalizeAccountKey(acc2);
  return k1 !== '' && k2 !== '' && k1 === k2;
}

/**
 * The columns shown on the upload screen.
 *
 * THIS LIST IS WHAT THE SCREEN PROMISES, so it now matches the template that
 * the Download button actually produces. It did not: it advertised CITY and
 * COORDINATES, which no bank sends and which the template never contained,
 * and it left out CIF, which the importer has always read and which is the
 * strongest link between a customer's several loans.
 *
 * COORDINATES and CITY are still READ when a sheet happens to carry them --
 * that costs nothing and loses nothing. They are simply no longer presented
 * as something to supply.
 */
export const REQUIRED_EXCEL_COLUMNS = [
  'ZONE',
  'AREA',
  'BRANCH_COD',
  'BRANCH_NAME',
  'ACCT_NO',
  'CIF',
  'NAME',
  'PROD_DESC',
  'FACILITY',
  'LIMIT_SANCTI',
  'LOAN_B',
  'NPA_DT',
  'CONTACT NO.',
  /* CUSTOMER_BALANCE IS NOT ASKED FOR, AND MUST NOT BE.
     It is the borrower's TOTAL across every loan they hold, repeated on each
     of their rows. Accepting it as a loan balance is what once imported every
     account of a multi-loan customer at the customer's full exposure. The
     app works the customer's total out from the loans themselves, which is
     the only version that stays right after a collection. Putting it on this
     list would invite a bank to send it. */
  'ADDRESS',
  'AGENT_ID',
  'AGENT_NAME',
  'RISK_CLASSIFICATION',
  'LATEST_REMARK',
];

/**
 * EVERY SPELLING A BANK USES FOR ONE LOAN'S OWN BALANCE.
 *
 * Add new spellings HERE and nowhere else -- the parser, the template check
 * and the pre-import guard all read this one list, so they cannot disagree
 * about what counts as a balance column.
 *
 * `CUSTOMER_BALANCE` IS DELIBERATELY ABSENT and must never be added. It
 * carries the borrower's total across all their loans, repeated on each of
 * their rows. Accepting it here is what caused every account of a multi-loan
 * customer to be imported at the customer's full exposure.
 */
export const LOAN_BALANCE_COLUMNS = [
  'LOAN_B',
  'LOAN_BAL',
  'LOANBAL',
  'LOAN_BALANCE',
  'LOAN_OS',
  'OUTSTANDING',
  'OUTSTANDING_AMOUNT',
  'OUTSTANDING_BALANCE',
  'OS_BALANCE',
  'POS',
  'POS_BALANCE',
  'PRINCIPAL_OS',
  'PRINCIPAL_OUTSTANDING',
  'BALANCE',
  'ACCOUNT_BALANCE',
  'LEDGER_BALANCE',
];

/** Every spelling a bank uses for the sanctioned limit. */
export const SANCTION_COLUMNS = [
  'LIMIT_SANCTI',
  'LIMIT_SANCTION_AM',
  'LIMIT_SANCTION_AMT',
  'LIMIT_SANCTION_AMOUNT',
  'LIMIT_SANCTIONED',
  'SANCTION_LIMIT',
  'SANCTION_AMOUNT',
  'SANCTIONED_AMOUNT',
  'LOAN_AMOUNT',
  'LIMIT',
];

/**
 * The borrower's TOTAL across every loan they hold. Read for reference only
 * -- never as one account's balance. The figure the app uses is the sum of
 * the borrower's loans, which is the only version that stays right when a
 * loan is collected on.
 */
export const CUSTOMER_TOTAL_COLUMNS = [
  'CUSTOMER_BALANCE',
  'CUSTOMER_BAL',
  'CUST_BALANCE',
  'TOTAL_BALANCE',
  'TOTAL_OUTSTANDING',
];

/**
 * Map raw row values into structured Account object
 */
export function mapExcelRowToAccount(
  row: ParsedExcelRow,
  defaultAgentId?: string,
  defaultAgentName?: string,
  coordinatorName: string = 'Kavita Joshi'
): Account {
  const accountId = row.accountNo ? (row.accountNo.startsWith('ACC-') ? row.accountNo : `ACC-${row.accountNo}`) : `ACC-${Date.now()}`;
  
  // Clean phone number
  let phone = String(row.contactNo || '').trim();
  if (phone && !phone.startsWith('+91') && phone.replace(/\D/g, '').length === 10) {
    const raw = phone.replace(/\D/g, '');
    phone = `+91 ${raw.slice(0, 5)} ${raw.slice(5)}`;
  } else if (!phone) {
    phone = 'N/A';
  }

  /**
   * THE BANK'S OWN WORDS ARE KEPT.
   *
   * THE BUG THIS FIXES: the sheet's PROD_DESC was read, matched against seven
   * built-in categories by keyword, and then THROWN AWAY -- only the category
   * was stored. Anything the keywords did not recognise silently became
   * "Personal Loan". So a Cash Credit, a Term Loan, a KCC, a Mudra loan and an
   * SHG loan all displayed as Personal Loan, and the screen disagreed with the
   * spreadsheet it came from.
   *
   * The matching itself was unsafe too. Two-letter codes were tested as
   * substrings, so any description containing the letters "hl", "bl" or "cc"
   * anywhere inside a word was miscategorised.
   *
   * Now the description from the sheet is stored exactly as written and is
   * what every screen shows. The category is still worked out, because
   * filtering by kind of loan is useful, but it is a label ALONGSIDE the real
   * text rather than a replacement for it -- and it is matched on whole words.
   */
  const productDescription = (row.productDesc || '').trim();
  const prodUpper = productDescription.toUpperCase();
  const hasWord = (w: string) =>
    new RegExp(`(^|[^A-Z0-9])${w}([^A-Z0-9]|$)`).test(prodUpper);

  let loanType: LoanType = 'Personal Loan';
  if (prodUpper.includes('TWO WHEELER') || hasWord('2W') || prodUpper.includes('BIKE')) {
    loanType = 'Two Wheeler Loan';
  } else if (prodUpper.includes('AUTO') || prodUpper.includes('CAR') || prodUpper.includes('VEHICLE')) {
    loanType = 'Auto Loan';
  } else if (prodUpper.includes('HOME') || prodUpper.includes('HOUSING') || hasWord('HL')) {
    loanType = 'Home Loan';
  } else if (
    prodUpper.includes('BUSINESS') ||
    prodUpper.includes('COMMERCIAL') ||
    prodUpper.includes('CASH CREDIT') ||
    hasWord('CC') ||
    hasWord('OD') ||
    hasWord('BL')
  ) {
    loanType = 'Business Loan';
  } else if (prodUpper.includes('GOLD')) {
    loanType = 'Gold Loan';
  } else if (prodUpper.includes('CARD') || prodUpper.includes('CREDIT CARD')) {
    loanType = 'Credit Card';
  }

  const outstanding = Number(row.loanBalance) || 0;
  const sanction = Number(row.limitSanctioned) || outstanding || 100000;
  const emi = 0; // NPA Customer Recovery

  // Category determination / Risk Classification
  let category: CustomerCategory = 'Medium Risk';
  if (row.riskClassification) {
    const rc = row.riskClassification.toLowerCase();
    if (rc.includes('critical') || rc.includes('npa') || rc.includes('defaulter')) {
      category = 'Critical NPA';
    } else if (rc.includes('high')) {
      category = 'High Risk';
    } else if (rc.includes('low')) {
      category = 'Low Risk';
    } else if (rc.includes('skip') || rc.includes('trace')) {
      category = 'Skip / Trace Required';
    }
  } else if (row.npaDate && (row.npaDate.includes('2022') || row.npaDate.includes('2021') || row.npaDate.includes('2020'))) {
    category = 'Critical NPA';
  } else if (outstanding > 500000) {
    category = 'High Risk';
  } else if (outstanding < 50000) {
    category = 'Low Risk';
  }

  const agentId = row.assignedAgentId || defaultAgentId || 'N/A';
  const agentName = row.assignedAgentName || defaultAgentName || (agentId !== 'N/A' ? 'Assigned Officer' : 'Unassigned');

  const initialNoteText = row.latestRemark
    ? row.latestRemark
    : row.isMultipleAccount
    ? `Customer has ${row.multipleAccountsCount} linked loan accounts. Facility: ${row.facility || 'N/A'}, NPA Date: ${row.npaDate || 'N/A'}, Balance: ₹${outstanding.toLocaleString('en-IN')}.`
    : `Master Account record imported. Facility: ${row.facility || 'N/A'}, NPA Date: ${row.npaDate || 'N/A'}, Balance: ₹${outstanding.toLocaleString('en-IN')}.`;

  const initialNotesHistory = [
    {
      id: `NOTE-INIT-${row.accountNo || Date.now()}`,
      agentId: agentId !== 'N/A' ? agentId : 'SYS',
      agentName: agentName !== 'Unassigned' ? agentName : 'System Importer',
      date: todayLocal(),
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      note: initialNoteText,
      category: 'General',
    },
  ];

  const assignedZone = row.zone?.trim() || (row.branchName ? `${row.branchName.split(' ')[0]} Zone` : 'N/A');
  const assignedArea = row.area?.trim() || (row.branchName ? `${row.branchName.split(' ')[0]} Area` : 'N/A');
  const assignedCity = row.city?.trim() || (row.branchName ? row.branchName.split(' ')[0] : 'N/A');

  // Coordinates come from the sheet or not at all.
  //
  // This used to be `row.latitude || 19.8762 + (Math.random() - 0.5) * 0.05` --
  // so EVERY imported account without coordinates got a random point within
  // about 5 km of one spot in Chhatrapati Sambhajinagar. Those are the
  // coordinates the customer window showed, the Google Maps link opened, and
  // field-visit routing used. They were fiction, and nothing said so.
  const latRaw = Number(row.latitude);
  const lngRaw = Number(row.longitude);
  const lat = Number.isFinite(latRaw) && latRaw !== 0 ? latRaw : undefined;
  const lng = Number.isFinite(lngRaw) && lngRaw !== 0 ? lngRaw : undefined;
  const coords =
    row.coordinates ||
    (lat !== undefined && lng !== undefined ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : '');

  return {
    id: accountId,
    accountId,
    loanNumber: row.accountNo || `LN-${Math.floor(10000 + Math.random() * 90000)}`,
    customerName: row.customerName || 'N/A',
    mobile: phone,
    address: row.address || 'N/A',
    city: assignedCity,
    branch: row.branchName || 'Main Branch',
    branchCode: row.branchCode || 'BR-01',
    facility: row.facility || 'Term Loan',
    npaDate: row.npaDate || 'N/A',
    customerCode: row.customerId || `CUST-${accountId}`,
    area: assignedArea,
    zone: assignedZone,
    coordinates: coords,
    loanType,
    /** Exactly as the bank wrote it in PROD_DESC. */
    productDescription: productDescription || undefined,
    sanctionAmount: sanction,
    outstandingAmount: outstanding,
    customerBalance: outstanding,
    overdueAmount: outstanding,
    emi,
    ptpCount: 0,
    customerCategory: category,
    riskClassification: category,
    accountCategory: row.accountCategory,
    accountStatus: 'Active',
    assignedAgentId: agentId,
    assignedAgentName: agentName,
    coordinatorId: 'USR-COORD-1',
    coordinatorName,
    allocationDate: todayLocal(),
    totalRecovered: 0,
    latitude: lat,
    longitude: lng,
    notes: initialNoteText,
    latestRemark: row.latestRemark || initialNoteText,
    isMultipleAccount: row.isMultipleAccount,
    multipleAccountsCount: row.multipleAccountsCount,
    linkedAccountIds: row.linkedAccountNumbers,
    multipleAccountReason: row.multipleAccountReason,
    agentNotesHistory: initialNotesHistory,
    customFields: row.raw || {},
  };
}

/**
 * Parses File (Excel / CSV) into structured data with exact column header recognition
 * Detects multiple accounts when customer balance and NPA date are identical or customer name matches
 */
export async function parseExcelOrCsvFile(file: File): Promise<ExcelImportResult> {
  const data = await file.arrayBuffer();
  /**
   * DATE CELLS ARRIVE AS THEIR RAW NUMBER, NOT AS A CLOCK TIME.
   *
   * `cellDates: true` asked the library to build a Date for every date cell.
   * A Date is a precise moment, so the library had to invent a time of day
   * and a time zone for a value that has neither -- and its base date sits in
   * 1899, when India's clock was +5:21 rather than +5:30, so every date came
   * back 8 minutes 50 seconds past midnight. Harmless in India; a full day
   * out anywhere west of Greenwich.
   *
   * Off, the cell arrives as the whole number Excel actually stores -- 43467
   * -- and `sheetDateToString` turns that into a day with integer arithmetic.
   * Same spreadsheet, same answer, on every machine.
   */
  const workbook = XLSX.read(data, { type: 'array', cellDates: false });
  
  // Pick first sheet
  const firstSheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[firstSheetName];
  
  // Convert sheet to JSON array of objects
  const rawRows: Record<string, any>[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
  
  if (!rawRows || rawRows.length === 0) {
    throw new Error('The uploaded file is empty. Please ensure the Excel sheet contains data rows.');
  }

  // Detect column names from the first few rows
  const allDetectedKeys = new Set<string>();
  rawRows.forEach((r) => {
    Object.keys(r).forEach((k) => allDetectedKeys.add(k));
  });

  // Strict Excel Template Format Validation
  // Ensure the uploaded sheet contains the required ScaleSupport Allocation template format
  const normalizedHeaders = Array.from(allDetectedKeys).map(normalizeKey);
  const hasAccountCol = normalizedHeaders.some((h) =>
    ['ACCT_NO', 'ACCOUNT_NO', 'ACCOUNT_NUMBER', 'ACCTNO', 'ACC_NO', 'LOAN_NO', 'LOAN_NUMBER'].includes(h)
  );
  const hasNameCol = normalizedHeaders.some((h) =>
    ['NAME', 'CUSTOMER_NAME', 'BORROWER_NAME', 'CUST_NAME', 'ACCOUNT_NAME'].includes(h)
  );
  /**
   * A PER-LOAN BALANCE COLUMN IS NOW MANDATORY, AND THE CUSTOMER TOTAL DOES
   * NOT SATISFY IT.
   *
   * This check used to accept CUSTOMER_BALANCE -- or even LIMIT_SANCTI, the
   * sanctioned limit -- as proof that the sheet carried a balance. A sheet
   * with no per-loan balance column therefore sailed through, and every
   * account was imported at the borrower's total or at its sanctioned limit.
   *
   * The next bank will spell this column differently again. When that
   * happens the import must STOP and say so, naming what it did find, rather
   * than quietly importing money that is wrong.
   */
  const hasBalanceCol = normalizedHeaders.some((h) => LOAN_BALANCE_COLUMNS.includes(h));

  if (!hasAccountCol || !hasNameCol || !hasBalanceCol) {
    const missingColumns: string[] = [];
    if (!hasAccountCol) missingColumns.push('ACCT_NO / Account Number');
    if (!hasNameCol) missingColumns.push('NAME / Customer Name');

    if (!hasBalanceCol) {
      // The customer total is the likeliest thing to be there instead, and
      // mistaking one for the other is the whole reason this check exists.
      const customerTotalPresent = normalizedHeaders.filter((h) =>
        CUSTOMER_TOTAL_COLUMNS.includes(h)
      );

      throw new Error(
        'This sheet has no PER-LOAN BALANCE column, so it cannot be imported.\n\n' +
          'Each loan must carry its OWN outstanding balance. Columns recognised for ' +
          `that: ${LOAN_BALANCE_COLUMNS.join(', ')}.\n\n` +
          `Columns found in your sheet: ${normalizedHeaders.join(', ')}.\n\n` +
          (customerTotalPresent.length > 0
            ? `"${customerTotalPresent.join('", "')}" was found, but that is the CUSTOMER'S TOTAL ` +
              'across all their loans -- the same figure repeated on every row for that borrower. ' +
              'Importing it as a loan balance would give each of a customer\'s loans their whole ' +
              'exposure, inflating balances, OTS offers, collections and commission.\n\n'
            : '') +
          'Rename your balance column to LOAN_B (or LOAN_BAL), or send me the column name and ' +
          'I will add it.'
      );
    }

    throw new Error(
      `Invalid Excel Template Format! The uploaded file does not match the official ScaleSupport Allocation format. Missing mandatory template columns: ${missingColumns.join(', ')}. Upload aborted. Please download and use the provided Excel Template.`
    );
  }

  const parsedRows: ParsedExcelRow[] = [];

  rawRows.forEach((row, index) => {
    // Look up normalized keys
    const normalizedRow: Record<string, any> = {};
    Object.entries(row).forEach(([k, v]) => {
      normalizedRow[normalizeKey(k)] = v;
    });

    // Helper lookup with fuzzy matching
    const findVal = (...aliases: string[]) => {
      for (const alias of aliases) {
        const norm = normalizeKey(alias);
        if (normalizedRow[norm] !== undefined && normalizedRow[norm] !== '') {
          return normalizedRow[norm];
        }
      }
      return '';
    };

    const zone = String(findVal('ZONE', 'ZONE_NAME', 'ZONENAME', 'REGION', 'ZONE_DESC', 'ZONE_CODE')).trim();
    const area = String(findVal('AREA', 'AREA_NAME', 'AREANAME', 'DISTRICT', 'LOCATION', 'CLUSTER')).trim();
    const city = String(findVal('CITY', 'TOWN', 'CITY_NAME', 'MUNICIPALITY')).trim();
    const rawCoords = String(findVal('COORDINATES', 'COORDS', 'GPS_COORDINATES', 'GEO_LOCATION', 'LAT_LNG', 'LOCATION_COORDS')).trim();
    const rawLat = findVal('LATITUDE', 'LAT');
    const rawLng = findVal('LONGITUDE', 'LNG', 'LON', 'LONG');
    
    let lat: number | undefined = undefined;
    let lng: number | undefined = undefined;
    let coords: string | undefined = rawCoords || undefined;

    if (rawLat && rawLng) {
      lat = parseFloat(String(rawLat)) || undefined;
      lng = parseFloat(String(rawLng)) || undefined;
      if (lat && lng) {
        coords = `${lat}, ${lng}`;
      }
    } else if (rawCoords && rawCoords.includes(',')) {
      const parts = rawCoords.split(',');
      lat = parseFloat(parts[0].trim()) || undefined;
      lng = parseFloat(parts[1].trim()) || undefined;
    }

    const branchCode = String(findVal('BRANCH_COD', 'BRANCH_CODE', 'BRANCHCODE', 'BR_CODE', 'BRANCH')).trim();
    const branchName = String(findVal('BRANCH_NAME', 'BRANCHNAME', 'BRANCH_DESC', 'BRANCH')).trim();
    const accountNo = String(findVal('ACCT_NO', 'ACCOUNT_NO', 'ACCOUNT_NUMBER', 'ACCTNO', 'ACC_NO', 'LOAN_NO', 'LOAN_NUMBER')).trim();
    const customerName = String(findVal('CUSTOMER_NAME', 'NAME', 'BORROWER_NAME', 'CUST_NAME', 'ACCOUNT_NAME')).trim();
    const productDesc = String(findVal('PROD_DESC', 'PRODUCT_DESC', 'PRODUCT_DESCRIPTION', 'PRODUCT', 'LOAN_TYPE')).trim();
    const facility = String(findVal('FACILITY', 'FACILITY_TYPE', 'LOAN_SCHEME', 'SUB_PRODUCT')).trim();
    
    // Numbers
    const rawLimit = findVal(...SANCTION_COLUMNS);
    const limitSanctioned = typeof rawLimit === 'number' ? rawLimit : parseFloat(String(rawLimit).replace(/[^0-9.]/g, '')) || 0;

    /**
     * THE LOAN'S OWN BALANCE. NEVER THE CUSTOMER'S TOTAL.
     *
     * THE BUG THIS FIXES, AND IT WAS A BAD ONE.
     *
     * `CUSTOMER_BALANCE` used to sit at the END of this alias list, as a
     * last resort. A bank whose sheet spells the balance column in a way
     * this list did not know -- Soegaon writes LOAN_BAL, not LOAN_B --
     * matched none of the earlier names, fell through to CUSTOMER_BALANCE,
     * and matched THAT. With no error.
     *
     * CUSTOMER_BALANCE is the borrower's TOTAL across every loan they hold,
     * repeated on each of their rows. So every account of a multi-loan
     * customer was imported carrying the customer's whole exposure as its
     * own balance:
     *
     *     Aasefabi Shaikh Bhura   LOAN_BAL 75,000 and 41,486
     *                             CUSTOMER BALANCE 1,16,486 on both rows
     *                             imported as 1,16,486 and 1,16,486
     *
     *     ANANTRAO S DESHMUKH     two loans, both imported at 9,95,321
     *
     * Single-loan customers were unaffected, because for them the two
     * columns hold the same figure -- which is exactly why this went
     * unnoticed. It inflated portfolio totals, the OTS base, the amount an
     * agent could collect against one loan, and the commission earned on it.
     *
     * CUSTOMER_BALANCE IS NO LONGER ACCEPTED HERE AT ALL. It is a
     * customer-level figure; the customer total is worked out by adding the
     * borrower's loans together, which is the only way it can be right.
     */
    const rawBalance = findVal(...LOAN_BALANCE_COLUMNS);
    const loanBalance = typeof rawBalance === 'number' ? rawBalance : parseFloat(String(rawBalance).replace(/[^0-9.]/g, '')) || 0;

    // Date
    let npaDate = findVal('NPA_DT', 'NPA_DATE', 'NPADATE', 'DATE_OF_NPA');
    // Copied across, not converted. A date cell is a DAY -- no clock, no
    // time zone. See sheetDateToString; two earlier attempts at this went
    // wrong because both put the value through a Date first.
    npaDate = sheetDateToString(npaDate as any);

    const contactNo = String(findVal('CONTACT NO.', 'CONTACT_NO', 'CONTACT_NUMBER', 'MOBILE', 'PHONE', 'MOBILE_NO', 'CELL')).trim();
    const customerId = String(findVal('CUSTOMER_ID', 'CUSTOMER_NO', 'CIF', 'CIF_NO', 'CUST_ID')).trim();
    const address = String(findVal('ADDRESS', 'CUSTOMER_ADDRESS', 'RESIDENCE_ADDRESS', 'BORROWER_ADDRESS')).trim();

    // Risk Classification & Remarks
    const riskClassification = String(findVal('RISK_CLASSIFICATION', 'CUSTOMER_RISK', 'RISK', 'CLASSIFICATION', 'CATEGORY', 'CUSTOMER_CATEGORY')).trim() || undefined;
    const latestRemark = String(findVal('LATEST_REMARK', 'REMARK', 'REMARKS', 'AGENT_REMARKS', 'NOTES', 'AGENT_NOTE')).trim() || undefined;
    // The bank's own account category (SMA 0, SMA 1, SMA 2, NPA). Not the same as
    // CATEGORY above, which is our risk classification.
    const accountCategory = String(findVal('ACCOUNT_CATEGORY', 'ACCT_CATEGORY', 'ACCOUNT_CATEGORY_NAME')).trim() || undefined;

    // Allocation columns if provided
    const assignedAgentId =
      String(
        findVal(
          'AGENT_ID',
          'USER_ID',
          'USERID',
          'AGENT_CODE',
          'AGENTCODE',
          'USER_CODE',
          'ASSIGNED_AGENT_ID',
          'ASSIGNED_USER_ID',
          'RECOVERY_AGENT_ID',
          'OFFICER_ID',
          'STAFF_ID'
        )
      ).trim() || undefined;

    const assignedAgentName =
      String(
        findVal(
          'AGENT_NAME',
          'USER_NAME',
          'USERNAME',
          'ASSIGNED_AGENT_NAME',
          'ASSIGNED_USER_NAME',
          'OFFICER_NAME',
          'STAFF_NAME',
          'AGENT_DESC'
        )
      ).trim() || undefined;

    const errors: string[] = [];
    if (!accountNo) errors.push('Missing Account Number (ACCT_NO)');
    if (!customerName) errors.push('Missing Customer Name (NAME)');
    if (!loanBalance && !limitSanctioned) errors.push('Missing Loan Balance');

    parsedRows.push({
      rowNumber: index + 2,
      raw: row,
      zone: zone || undefined,
      area: area || undefined,
      city: city || undefined,
      coordinates: coords,
      latitude: lat,
      longitude: lng,
      branchCode: branchCode || 'BR-01',
      branchName: branchName || 'Main Branch',
      accountNo: accountNo || `TEMP-${index + 1}`,
      customerName: customerName || 'N/A',
      productDesc: productDesc || 'Personal Loan',
      facility: facility || 'Term Loan',
      limitSanctioned: limitSanctioned || loanBalance,
      loanBalance: loanBalance || limitSanctioned,
      customerBalance: loanBalance,
      npaDate: npaDate || 'N/A',
      contactNo: contactNo || 'N/A',
      customerId: customerId || `CUST-${accountNo}`,
      address: address || 'N/A',
      assignedAgentId,
      assignedAgentName,
      riskClassification,
      accountCategory,
      latestRemark,
      isValid: errors.length === 0,
      validationErrors: errors,
    });
  });

  // MULTI-ACCOUNT DEDUPLICATION & GROUPING:
  // Safe grouping: Only group accounts that genuinely belong to the same customer
  // 1) Explicit customerId / CIF match
  // 2) Exact customer name + 10-digit mobile number match
  const groupMap = new Map<string, ParsedExcelRow[]>();

  parsedRows.forEach((row) => {
    const normName = row.customerName.toLowerCase().replace(/[^a-z0-9]/g, '');
    const cleanPhone = row.contactNo.replace(/\D/g, '');
    const cleanCif = row.customerId ? row.customerId.toUpperCase().trim() : '';

    let key = '';
    if (cleanCif && !cleanCif.startsWith('CUST-TEMP-') && cleanCif.length >= 3) {
      key = `CIF:${cleanCif}`;
    } else if (normName && normName.length >= 4 && cleanPhone.length >= 10) {
      key = `NAME_PHONE:${normName}__${cleanPhone}`;
    } else {
      key = `SINGLE:${row.accountNo}`;
    }

    const list = groupMap.get(key) || [];
    list.push(row);
    groupMap.set(key, list);
    row.multipleAccountGroupKey = key;
  });

  // Tag rows that have multiple accounts in the group
  let multiAccountRowsCount = 0;
  const multiAccountCustomerKeys = new Set<string>();

  groupMap.forEach((rowsInGroup, key) => {
    if (rowsInGroup.length > 1) {
      multiAccountCustomerKeys.add(key);
      const linkedAccNos = rowsInGroup.map((r) => r.accountNo);
      
      rowsInGroup.forEach((r) => {
        multiAccountRowsCount++;
        r.isMultipleAccount = true;
        r.multipleAccountsCount = rowsInGroup.length;
        r.linkedAccountNumbers = linkedAccNos;
        r.multipleAccountReason = `Customer has ${rowsInGroup.length} linked loan accounts`;
      });
    } else {
      rowsInGroup[0].isMultipleAccount = false;
      rowsInGroup[0].multipleAccountsCount = 1;
      rowsInGroup[0].linkedAccountNumbers = [rowsInGroup[0].accountNo];
    }
  });

  return {
    fileName: file.name,
    totalRows: parsedRows.length,
    validRows: parsedRows.filter((r) => r.isValid).length,
    invalidRows: parsedRows.filter((r) => !r.isValid).length,
    multipleAccountsCount: multiAccountRowsCount,
    multipleCustomersCount: multiAccountCustomerKeys.size,
    rows: parsedRows,
    detectedColumns: Array.from(allDetectedKeys),
  };
}

/**
 * Generates and triggers download of sample Excel file in EXACT format requested:
 * BRANCH_COD | BRANCH_NAME | ACCT_NO | CIF | NAME | PROD_DESC | FACILITY | LIMIT_SANCTI | LOAN_B | NPA_DT | CONTACT NO. | CUSTOMER_BALANCE | ADDRESS | AGENT_ID | AGENT_NAME
 * Note: CUSTOMER_BALANCE represents the customer's total overdue balance for NPA recovery.
 *
 * CIF IS OPTIONAL BUT IT IS THE STRONGEST LINK THERE IS.
 *
 * Accounts sharing a CIF are one customer: their loans are grouped and an
 * OTS is worked out on the COMBINED outstanding under the EARLIEST NPA date.
 * The importer has always read it -- from CIF, CIF_NO, CUSTOMER_ID,
 * CUSTOMER_NO or CUST_ID -- but the template never asked for it, so the
 * strongest rule had nothing to work with and linking fell back to mobile,
 * or to name-and-balance together.
 *
 * Leave the column out, or leave cells blank, and nothing changes: each
 * account keeps a placeholder code of its own so unrelated borrowers can
 * never be grouped by accident.
 *
 * The first two sample rows share one CIF on purpose -- that is a customer
 * with two loans.
 */
/**
 * Is this customer code the one the importer invents, rather than the bank's?
 *
 * A sheet with no CIF column leaves every account without a customer
 * identifier. The importer gives each one `CUST-<account number>` -- unique
 * per account, so blank-CIF borrowers can never be grouped together by
 * accident. That is deliberate and it must stay.
 *
 * But it is not a CIF, and it should not be shown or exported as one.
 */
export function isPlaceholderCustomerCode(
  customerCode?: string | null,
  accountId?: string | null
): boolean {
  const code = (customerCode || '').trim();
  if (!code) return true;
  if (!/^CUST-/i.test(code)) return false;
  const tail = code.slice(5).trim().toLowerCase();
  const acct = (accountId || '').trim().toLowerCase();
  // "CUST-" plus this account's own number is the invented one.
  return !acct || tail === acct;
}

export function downloadExactExcelTemplate() {
  const sampleData = [
    {
      ZONE: 'North Zone Maharashtra',
      AREA: 'Chhatrapati Sambhajinagar (Aurangabad)',
      BRANCH_COD: '104',
      BRANCH_NAME: 'Chhatrapati Sambhajinagar Main Branch',
      ACCT_NO: '00291040001892',
      ACCOUNT_CATEGORY: 'SMA 1',
      CIF: 'CIF10044821',
      NAME: 'Rajesh Narayan Patil',
      PROD_DESC: 'Personal Loan',
      FACILITY: 'Term Loan',
      LIMIT_SANCTI: 350000,
      LOAN_B: 245000,
      NPA_DT: '2023-10-31',
      'CONTACT NO.': '9822019482',
      CUSTOMER_BALANCE: 245000, // Total Overdue Balance for NPA Customer
      ADDRESS: 'Plot 48, Samarth Nagar, Chhatrapati Sambhajinagar, MH 431001',
      AGENT_ID: 'RA-0045',
      AGENT_NAME: 'Rajesh Shinde',
    },
    {
      ZONE: 'North Zone Maharashtra',
      AREA: 'Chhatrapati Sambhajinagar (Aurangabad)',
      BRANCH_COD: '104',
      BRANCH_NAME: 'Chhatrapati Sambhajinagar Main Branch',
      ACCT_NO: '00291040001893',
      ACCOUNT_CATEGORY: 'SMA 2',
      CIF: 'CIF10044821',
      NAME: 'Rajesh Narayan Patil',
      PROD_DESC: 'Two Wheeler Loan',
      FACILITY: 'Hypothecation',
      LIMIT_SANCTI: 245000,
      LOAN_B: 245000,
      NPA_DT: '2023-10-31',
      'CONTACT NO.': '9822019482',
      CUSTOMER_BALANCE: 245000, // Matching Name + NPA Date + Balance -> 1 Customer Multi-Account
      ADDRESS: 'Plot 48, Samarth Nagar, Chhatrapati Sambhajinagar, MH 431001',
      AGENT_ID: 'RA-0045',
      AGENT_NAME: 'Rajesh Shinde',
    },
    {
      ZONE: 'North Zone Maharashtra',
      AREA: 'Chhatrapati Sambhajinagar (Aurangabad)',
      BRANCH_COD: '105',
      BRANCH_NAME: 'Mill Corner Branch',
      ACCT_NO: '00291050002145',
      ACCOUNT_CATEGORY: 'SMA 0',
      CIF: 'CIF10052319',
      NAME: 'Sunil Baburao Shinde',
      PROD_DESC: 'Two Wheeler Loan',
      FACILITY: 'Hypothecation',
      LIMIT_SANCTI: 85000,
      LOAN_B: 42000,
      NPA_DT: '2024-01-15',
      'CONTACT NO.': '9822459012',
      CUSTOMER_BALANCE: 42000,
      ADDRESS: 'Flat 302, Sai Residency, Mill Corner, Chhatrapati Sambhajinagar',
      AGENT_ID: 'RA-0089',
      AGENT_NAME: 'Prakash Jadhav',
    },
    {
      ZONE: 'West Zone Maharashtra',
      AREA: 'Pune Metro',
      BRANCH_COD: '201',
      BRANCH_NAME: 'Pune Camp Branch',
      ACCT_NO: '00292010008891',
      ACCOUNT_CATEGORY: 'NPA',
      CIF: 'CIF10060744',
      NAME: 'Pooja Anand Deshmukh',
      PROD_DESC: 'Business Loan',
      FACILITY: 'Cash Credit',
      LIMIT_SANCTI: 750000,
      LOAN_B: 580000,
      NPA_DT: '2023-08-30',
      'CONTACT NO.': '9422781903',
      CUSTOMER_BALANCE: 580000,
      ADDRESS: 'Shop No 12, MG Road Commercial Complex, Pune Camp, Pune 411001',
      AGENT_ID: 'RA-0112',
      AGENT_NAME: 'Amit Kulkarni',
    },
    {
      ZONE: 'West Zone Maharashtra',
      AREA: 'Pune Metro',
      BRANCH_COD: '201',
      BRANCH_NAME: 'Pune Camp Branch',
      ACCT_NO: '00292010009412',
      ACCOUNT_CATEGORY: 'SMA 1',
      CIF: '',
      NAME: 'Vikas Madhavrao Jagtap',
      PROD_DESC: 'Auto Loan',
      FACILITY: 'Vehicle Term Loan',
      LIMIT_SANCTI: 500000,
      LOAN_B: 310000,
      NPA_DT: '2024-02-28',
      'CONTACT NO.': '9890123490',
      CUSTOMER_BALANCE: 310000,
      ADDRESS: 'B-14, Green Valley Apts, Kothrud, Pune 411038',
      AGENT_ID: 'RA-0045',
      AGENT_NAME: 'Rajesh Shinde',
    },
    {
      ZONE: 'North Zone Maharashtra',
      AREA: 'Nashik Industrial Region',
      BRANCH_COD: '302',
      BRANCH_NAME: 'Nashik Road Branch',
      ACCT_NO: '00293020004519',
      ACCOUNT_CATEGORY: 'NPA',
      CIF: 'CIF10077105',
      NAME: 'Ganesh Trimbak Gaikwad',
      PROD_DESC: 'Home Loan',
      FACILITY: 'Housing Loan',
      LIMIT_SANCTI: 1800000,
      LOAN_B: 1420000,
      NPA_DT: '2023-06-30',
      'CONTACT NO.': '9765432109',
      CUSTOMER_BALANCE: 1420000,
      ADDRESS: 'Row House 5, Shanti Niketan, Nashik Road, Nashik 422101',
      AGENT_ID: 'RA-0156',
      AGENT_NAME: 'Suresh Patil',
    },
  ];

  const worksheet = XLSX.utils.json_to_sheet(sampleData);
  
  // Set column widths
  worksheet['!cols'] = [
    { wch: 24 }, // ZONE
    { wch: 28 }, // AREA
    { wch: 14 }, // BRANCH_COD
    { wch: 32 }, // BRANCH_NAME
    { wch: 22 }, // ACCT_NO
    { wch: 28 }, // NAME
    { wch: 20 }, // PROD_DESC
    { wch: 18 }, // FACILITY
    { wch: 16 }, // LIMIT_SANCTI
    { wch: 14 }, // LOAN_B
    { wch: 14 }, // NPA_DT
    { wch: 18 }, // CONTACT NO.
    { wch: 20 }, // CUSTOMER_BALANCE
    { wch: 45 }, // ADDRESS
    { wch: 14 }, // AGENT_ID
    { wch: 22 }, // AGENT_NAME
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Account_Allocation_Template');
  
  // Write and trigger download
  XLSX.writeFile(workbook, 'ScaleSupport_Account_Allocation_Template.xlsx');
}

/**
 * Export current accounts to Excel in the exact format
 */
export function exportAccountsToExactExcel(accounts: Account[], filename = 'ScaleSupport_Allocated_Accounts.xlsx') {
  const exportRows = accounts.map((acc) => ({
    ZONE: acc.zone || '',
    AREA: acc.area || '',
    BRANCH_COD: acc.branchCode || '104',
    BRANCH_NAME: acc.branch,
    ACCT_NO: acc.loanNumber || acc.accountId,
    /* The bank's own CIF, when the sheet that created this account carried
       one. The placeholder the importer invents for a blank CIF is NOT
       exported -- writing "CUST-60507620262" into a CIF column would make an
       invented code look like the bank's, and re-importing it would make it
       permanent. */
    CIF: isPlaceholderCustomerCode(acc.customerCode, acc.accountId)
      ? ''
      : acc.customerCode || '',
    NAME: acc.customerName,
    /* Round-trips the bank's own wording, so an export re-imported comes back
       identical. Exporting the derived category instead quietly rewrote
       PROD_DESC every time a sheet made the round trip. */
    PROD_DESC: productOf(acc),
    /* The bank's account category (SMA 0, SMA 1, SMA 2, NPA). Blank if not yet uploaded. */
    ACCOUNT_CATEGORY: acc.accountCategory || '',
    FACILITY: acc.facility || 'Term Loan',
    LIMIT_SANCTI: acc.sanctionAmount,
    LOAN_B: acc.outstandingAmount,
    NPA_DT: acc.npaDate || '2024-03-31',
    'CONTACT NO.': acc.mobile,
    CUSTOMER_BALANCE: acc.customerBalance || acc.overdueAmount || acc.outstandingAmount,
    ADDRESS: acc.address,
    // Extra operational columns
    ASSIGNED_AGENT: `${acc.assignedAgentId} (${acc.assignedAgentName})`,
    ACCOUNT_STATUS: acc.accountStatus,
    PTP_STATUS: acc.ptpStatus || 'None',
    TOTAL_RECOVERED: acc.totalRecovered,
  }));

  const worksheet = XLSX.utils.json_to_sheet(exportRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'ScaleSupport_Accounts');
  XLSX.writeFile(workbook, filename);
}

export interface BackendDataExportPayload {
  accounts: Account[];
  users: User[];
  recoveries: RecoveryRecord[];
  followups: FollowUp[];
  ptps: PTPRecord[];
  visits: FieldVisit[];
  photos: PhotoRecord[];
  voiceNotes: VoiceNoteRecord[];
  documents: DocumentRecord[];
  commissions: CommissionRecord[];
  activityLogs: ActivityLog[];
  allocationHistories: AllocationHistory[];
}

/**
 * Downloads ALL backend tables in a unified multi-sheet Excel workbook (.xlsx)
 */
export function exportAllBackendDataToExcel(
  data: BackendDataExportPayload,
  filename?: string
) {
  const {
    accounts,
    users,
    recoveries,
    followups,
    ptps,
    visits,
    photos,
    voiceNotes,
    documents,
    commissions,
    activityLogs,
    allocationHistories,
  } = data;

  const workbook = XLSX.utils.book_new();

  // 1. Accounts & Customers Master
  const accountsSheetData = accounts.map((a) => ({
    BRANCH_CODE: a.branchCode || 'BR-01',
    BRANCH_NAME: a.branch,
    ACCOUNT_ID: a.accountId,
    LOAN_NUMBER: a.loanNumber || a.accountId,
    CUSTOMER_NAME: a.customerName,
    CONTACT_MOBILE: a.mobile,
    ALTERNATE_MOBILE: a.alternateMobile || '',
    LOAN_PRODUCT: a.loanType,
    FACILITY: a.facility || a.loanType,
    SANCTION_LIMIT: a.sanctionAmount,
    OUTSTANDING_BALANCE: a.outstandingAmount,
    OVERDUE_AMOUNT: a.overdueAmount,
    CUSTOMER_BALANCE: a.customerBalance || a.overdueAmount || a.outstandingAmount,
    NPA_DATE: a.npaDate || '',
    REGISTERED_ADDRESS: a.address,
    CITY: a.city || '',
    AREA: a.area || '',
    ZONE: a.zone || '',
    ASSIGNED_AGENT_ID: a.assignedAgentId,
    ASSIGNED_AGENT_NAME: a.assignedAgentName,
    ACCOUNT_STATUS: a.accountStatus,
    PTP_COUNT: a.ptpCount || 0,
    PTP_STATUS: a.ptpStatus || 'None',
    TOTAL_RECOVERED: a.totalRecovered,
    LAST_FOLLOWUP_DATE: a.lastFollowUpDate || '',
    LAST_FOLLOWUP_STATUS: a.lastFollowUpStatus || '',
    NEXT_FOLLOWUP_DATE: a.nextFollowUpDate || '',
    LATEST_REMARKS: a.notes || '',
    AGENT_NOTES_COUNT: (a.agentNotesHistory || []).length,
  }));
  const wsAccounts = XLSX.utils.json_to_sheet(accountsSheetData);
  XLSX.utils.book_append_sheet(workbook, wsAccounts, '1_Accounts_Master');

  // 2. Recoveries & Payments
  const recoveriesSheetData = recoveries.map((r) => ({
    RECOVERY_ID: r.recoveryId || r.id,
    ACCOUNT_ID: r.accountId,
    CUSTOMER_NAME: r.customerName,
    AMOUNT_COLLECTED: r.amount,
    RECOVERY_DATE: r.recoveryDate,
    PAYMENT_MODE: r.paymentMode,
    RECEIPT_NUMBER: r.receiptNumber,
    TRANSACTION_REF: r.referenceNumber,
    AGENT_ID: r.agentId,
    AGENT_NAME: r.agentName,
    COMMISSION_PERCENT: r.commissionPercentage || 10,
    COMMISSION_AMOUNT: r.commissionAmount || Math.round(r.amount * 0.1),
    COMMISSION_STATUS: r.commissionPaidStatus || 'Approved',
    BRANCH: r.branch,
    ZONE: r.zone,
    REMARKS: r.remarks,
    PROOF_DRIVE_FILE_ID: r.proofDriveFileId || '',
  }));
  const wsRecoveries = XLSX.utils.json_to_sheet(recoveriesSheetData);
  XLSX.utils.book_append_sheet(workbook, wsRecoveries, '2_Recoveries_Payments');

  // 3. Followup Calls & Interaction Remarks History
  const followupsSheetData = followups.map((f) => ({
    FOLLOWUP_ID: f.id,
    ACCOUNT_ID: f.accountId,
    CUSTOMER_NAME: f.customerName,
    DATE: f.date,
    TIME: f.time,
    CALL_STATUS: f.status,
    CUSTOMER_RESPONSE: f.customerResponse,
    DISCUSSION_DETAILS: f.discussionDetails,
    AGENT_REMARKS: f.agentRemarks,
    NEXT_FOLLOWUP_DATE: f.nextFollowUpDate || '',
    NEXT_FOLLOWUP_TIME: f.nextFollowUpTime || '',
    AGENT_ID: f.agentId,
    AGENT_NAME: f.agentName,
  }));
  const wsFollowups = XLSX.utils.json_to_sheet(followupsSheetData);
  XLSX.utils.book_append_sheet(workbook, wsFollowups, '3_Followup_Calls_Remarks');

  // 4. PTP Commitments
  const ptpSheetData = ptps.map((p) => ({
    PTP_ID: p.id,
    ACCOUNT_ID: p.accountId,
    CUSTOMER_NAME: p.customerName,
    PROMISED_AMOUNT: p.amount,
    PTP_DATE: p.ptpDate,
    PAYMENT_MODE: p.ptpMode,
    STATUS: p.status,
    CUSTOMER_COMMITMENT: p.customerCommitment,
    AGENT_REMARKS: p.remarks,
    CREATED_AT: p.createdAt,
    RESOLVED_DATE: p.resolvedDate || '',
    AGENT_ID: p.agentId,
    AGENT_NAME: p.agentName,
  }));
  const wsPtps = XLSX.utils.json_to_sheet(ptpSheetData);
  XLSX.utils.book_append_sheet(workbook, wsPtps, '4_PTP_Commitments');

  // 5. Field Visits & GPS Logs
  const visitsSheetData = visits.map((v) => ({
    VISIT_ID: v.visitId || v.id,
    ACCOUNT_ID: v.accountId,
    CUSTOMER_NAME: v.customerName,
    VISIT_DATE: v.date,
    START_TIME: v.startTime || '',
    END_TIME: v.endTime || '',
    STATUS: v.visitStatus,
    LATITUDE: v.latitude,
    LONGITUDE: v.longitude,
    ACCURACY_METERS: v.accuracyMeters || 0,
    ADDRESS: v.address || '',
    CUSTOMER_INTERACTION: v.customerInteraction || '',
    VISIT_REMARKS: v.visitRemarks || '',
    PHOTOS_COUNT: (v.photoDriveFileIds || []).length,
    VOICE_NOTES_COUNT: (v.voiceNoteDriveFileIds || []).length,
    AGENT_ID: v.agentId,
    AGENT_NAME: v.agentName,
  }));
  const wsVisits = XLSX.utils.json_to_sheet(visitsSheetData);
  XLSX.utils.book_append_sheet(workbook, wsVisits, '5_Field_Visits_GPS');

  // 6. Watermarked Photos
  const photosSheetData = photos.map((p) => ({
    PHOTO_ID: p.photoId || p.id,
    ACCOUNT_ID: p.accountId,
    DATE: p.date,
    TIME: p.time,
    LATITUDE: p.latitude,
    LONGITUDE: p.longitude,
    AGENT_REMARK: p.agentRemark || '',
    AGENT_ID: p.agentId,
    AGENT_NAME: p.agentName,
    DRIVE_FILE_ID: p.driveFileId || '',
    WATERMARK_TEXT: p.watermarkText || '',
    UPLOAD_STATUS: p.uploadStatus || 'Uploaded',
  }));
  const wsPhotos = XLSX.utils.json_to_sheet(photosSheetData);
  XLSX.utils.book_append_sheet(workbook, wsPhotos, '6_Watermarked_Photos');

  // 7. Voice Recordings
  const voiceSheetData = voiceNotes.map((vn) => ({
    VOICE_NOTE_ID: vn.voiceNoteId || vn.id,
    ACCOUNT_ID: vn.accountId,
    TITLE: vn.title,
    DURATION_SECONDS: vn.durationSeconds || 0,
    DATE: vn.date,
    TIME: vn.time,
    TRANSCRIPTION: vn.transcription || '',
    AGENT_ID: vn.agentId,
    AGENT_NAME: vn.agentName,
    DRIVE_FILE_ID: vn.driveFileId || '',
  }));
  const wsVoice = XLSX.utils.json_to_sheet(voiceSheetData);
  XLSX.utils.book_append_sheet(workbook, wsVoice, '7_Voice_Recordings');

  // 8. Documents & KYC
  const docsSheetData = documents.map((d) => ({
    DOCUMENT_ID: d.documentId || d.id,
    ACCOUNT_ID: d.accountId,
    CUSTOMER_NAME: d.customerName || '',
    DOCUMENT_TYPE: d.documentType,
    FILE_NAME: d.fileName,
    FILE_SIZE_BYTES: d.fileSizeBytes,
    DATE_TIME: d.dateTime,
    UPLOADED_BY_ID: d.uploadedBy,
    UPLOADED_BY_NAME: d.uploadedByName,
    DRIVE_FILE_ID: d.driveFileId || '',
    FILE_URL: d.fileUrl || '',
  }));
  const wsDocs = XLSX.utils.json_to_sheet(docsSheetData);
  XLSX.utils.book_append_sheet(workbook, wsDocs, '8_KYC_Documents');

  // 9. Users & Agents Directory
  const usersSheetData = users.map((u) => ({
    USER_ID: u.id,
    USERNAME: u.username,
    FULL_NAME: u.name,
    ROLE: u.role.toUpperCase(),
    // Passwords are deliberately NOT exported. They live only in Firebase
    // Auth and are never readable by the app, so any value printed here
    // would be a guessable default -- a credential leak in a spreadsheet.
    LOGIN_ID: u.loginId || u.username || u.agentId || '',
    AGENT_ID: u.agentId || 'N/A',
    EMAIL: u.contactEmail || '',
    MOBILE: u.mobile,
    BRANCH: u.branch,
    AREA: u.area,
    ZONE: u.zone,
    MONTHLY_TARGET: u.monthlyTarget || '',
    JOINING_DATE: u.joiningDate,
    ACTIVE_STATUS: u.active ? 'ACTIVE' : 'INACTIVE',
  }));
  const wsUsers = XLSX.utils.json_to_sheet(usersSheetData);
  XLSX.utils.book_append_sheet(workbook, wsUsers, '9_Users_and_Agents');

  // 10. Commissions Ledger (10%)
  const commSheetData = commissions.map((c) => ({
    COMMISSION_ID: c.id,
    RECOVERY_ID: c.recoveryId,
    ACCOUNT_ID: c.accountId,
    AGENT_ID: c.agentId,
    AGENT_NAME: c.agentName,
    RECOVERY_AMOUNT: c.recoveryAmount,
    COMMISSION_RATE_PERCENT: c.commissionRate || 10,
    COMMISSION_AMOUNT: c.commissionAmount,
    DATE: c.date,
    STATUS: c.status,
    PAID_DATE: c.paidDate || '',
  }));
  const wsComm = XLSX.utils.json_to_sheet(commSheetData);
  XLSX.utils.book_append_sheet(workbook, wsComm, '10_Commissions_10Pct');

  // 11. Activity & Audit Trail
  const logsSheetData = activityLogs.map((l) => ({
    LOG_ID: l.id,
    TIMESTAMP: l.timestamp,
    USER_ID: l.userId,
    USER_NAME: l.userName,
    USER_ROLE: l.role,
    ACTION_TYPE: l.actionType,
    ACCOUNT_ID: l.accountId || '',
    DETAILS: l.details,
  }));
  const wsLogs = XLSX.utils.json_to_sheet(logsSheetData);
  XLSX.utils.book_append_sheet(workbook, wsLogs, '11_Activity_Audit_Trail');

  // 12. Account Allocation & Reallocation History
  const allocHistSheetData = allocationHistories.map((h) => ({
    HISTORY_ID: h.id,
    ACCOUNT_ID: h.accountId,
    PREVIOUS_AGENT_ID: h.previousAgentId,
    PREVIOUS_AGENT_NAME: h.previousAgentName,
    NEW_AGENT_ID: h.newAgentId,
    NEW_AGENT_NAME: h.newAgentName,
    REALLOCATED_BY: h.reallocatedBy,
    TIMESTAMP: h.date,
    REASON: h.reason,
  }));
  const wsAlloc = XLSX.utils.json_to_sheet(allocHistSheetData);
  XLSX.utils.book_append_sheet(workbook, wsAlloc, '12_Allocations_History');

  // 13. Historical Agent Remarks & Notes Log
  const allNotesList: any[] = [];
  accounts.forEach((acc) => {
    (acc.agentNotesHistory || []).forEach((note) => {
      allNotesList.push({
        NOTE_ID: note.id,
        ACCOUNT_ID: acc.accountId,
        LOAN_NUMBER: acc.loanNumber || acc.accountId,
        CUSTOMER_NAME: acc.customerName,
        DATE: note.date,
        TIME: note.time,
        AGENT_ID: note.agentId,
        AGENT_NAME: note.agentName,
        CATEGORY: note.category || 'General',
        REMARK_CONTENT: note.note,
      });
    });
  });
  const wsNotes = XLSX.utils.json_to_sheet(allNotesList);
  XLSX.utils.book_append_sheet(workbook, wsNotes, '13_Agent_Remarks_History');

  const todayStr = todayLocal();
  const finalFilename = filename || `SRMS_Complete_Backend_Database_${todayStr}.xlsx`;
  XLSX.writeFile(workbook, finalFilename);
}

/**
 * FULL BACKUP -- EVERYTHING THAT HAS ACTUALLY HAPPENED.
 *
 * This is the backup Ashish asked for, and it differs from the export above
 * in three ways he specified:
 *
 *   1. ACCOUNTS WITH NO ACTIVITY ARE LEFT OUT. A book of 200,000 allotted
 *      accounts where 3,000 have been worked produces a 3,000-row file
 *      instead of a 200,000-row one. Nothing is lost: an account with no
 *      collection, no follow-up, no PTP, no visit and no note holds nothing
 *      that is not already in the bank's own spreadsheet, which is where it
 *      came from. What cannot be re-imported from the bank is the WORK, and
 *      that is what this keeps.
 *
 *   2. BANKS, ZONES AND BRANCHES ARE INCLUDED. They were in neither export,
 *      so a restored file had collections filed against branches that no
 *      longer existed in the app.
 *
 *   3. EVERY ACTIVITY ROW CARRIES THE CUSTOMER NAME AND THE BRANCH beside
 *      the account number. An account number on its own is unreadable to a
 *      person: opening the file to check one agent's month meant looking
 *      every number up by hand.
 *
 * It is a DOWNLOAD, not a cloud copy. The file lands in the browser's
 * downloads folder and nothing is written to Firestore, so it costs no reads
 * beyond the data already loaded on screen.
 */
export function exportFullBackupToExcel(
  data: BackendDataExportPayload & {
    banks?: any[];
    zones?: any[];
    branches?: any[];
  },
  filename?: string
): { accountsIncluded: number; accountsSkipped: number; filename: string } {
  const {
    accounts,
    users,
    recoveries,
    followups,
    ptps,
    visits,
    photos,
    voiceNotes,
    documents,
    commissions,
    activityLogs,
    allocationHistories,
    banks = [],
    zones = [],
    branches = [],
  } = data;

  /**
   * Every key an account can be referred to by, so an account is never judged
   * inactive because its receipts were filed under its loan number rather
   * than its account id. Getting this wrong would DROP real work from a
   * backup, which is the one mistake a backup must not make.
   */
  const keysOf = (a: any): string[] =>
    [a?.accountId, a?.id, a?.loanNumber, a?.__docId]
      .filter((k): k is string => typeof k === 'string' && k.trim() !== '')
      .map((k) => k.trim().toLowerCase());

  const norm = (v: any): string => String(v ?? '').trim().toLowerCase();

  // One pass per activity table, building the set of accounts that have any.
  const active = new Set<string>();
  const mark = (rows: any[], field = 'accountId') => {
    (rows || []).forEach((r) => {
      const k = norm(r?.[field]);
      if (k) active.add(k);
    });
  };
  mark(recoveries);
  mark(followups);
  mark(ptps);
  mark(visits);
  mark(photos);
  mark(voiceNotes);
  mark(documents);
  mark(commissions);
  mark(allocationHistories);

  /**
   * HAS SOMEBODY ACTUALLY DONE SOMETHING ON THIS ACCOUNT?
   *
   * WHAT CHANGED, AND WHY. A backup of 5,897 accounts reported 0 left out,
   * which cannot be right on a freshly imported book. The reason was
   * `nextFollowUpDate`: the importer puts a follow-up date on every row it
   * creates, so every account looked worked-on and the backup included the
   * whole book. The test below now asks only for work a PERSON did:
   *
   *   a recovery, PTP, visit, photograph, voice note, document, commission
   *   entry or allocation (the tables above), an agent note, OTS history,
   *   an amount recovered, a closure date, or an OTS status that is not
   *   'None'.
   *
   * A follow-up date on its own no longer counts, because the import writes
   * one. LAST follow-up date still counts -- that one is only written when
   * somebody logs a follow-up.
   *
   * Nothing is lost by leaving an untouched account out: it can be
   * re-imported from the bank's own sheet. The work cannot, and every
   * record of work is still kept.
   */
  const hasActivity = (a: any): boolean => {
    if (keysOf(a).some((k) => active.has(k))) return true;
    // Work recorded on the account itself rather than in a separate table.
    if (Number(a?.totalRecovered) > 0) return true;
    if ((a?.agentNotesHistory || []).length > 0) return true;
    if ((a?.otsHistory || []).length > 0) return true;
    if (a?.lastFollowUpDate || a?.closureDate) return true;
    if (a?.otsStatus && a.otsStatus !== 'None') return true;
    return false;
  };

  const kept = (accounts || []).filter(hasActivity);
  const keptKeys = new Set<string>();
  kept.forEach((a) => keysOf(a).forEach((k) => keptKeys.add(k)));

  // Name and branch, looked up once rather than per row.
  const byKey = new Map<string, any>();
  (accounts || []).forEach((a) => keysOf(a).forEach((k) => byKey.set(k, a)));
  const nameOf = (accountId: any): string => byKey.get(norm(accountId))?.customerName || '';
  const branchOf = (accountId: any): string => byKey.get(norm(accountId))?.branch || '';

  /** Activity rows for the accounts being kept. */
  const mine = <T,>(rows: T[], field = 'accountId'): T[] =>
    (rows || []).filter((r: any) => {
      const k = norm(r?.[field]);
      // A row with no account on it is kept: losing it would lose the record.
      return !k || keptKeys.has(k);
    });

  const workbook = XLSX.utils.book_new();
  const add = (rows: any[], sheetName: string) => {
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(rows.length ? rows : [{ NOTE: 'No records' }]),
      sheetName
    );
  };

  add(
    kept.map((a: any) => ({
      ACCOUNT_ID: a.accountId,
      CUSTOMER_NAME: a.customerName,
      BRANCH_NAME: a.branch || '',
      BRANCH_CODE: a.branchCode || '',
      BANK: a.bank || '',
      ZONE: a.zone || '',
      LOAN_NUMBER: a.loanNumber || '',
      CONTACT_MOBILE: a.mobile || '',
      NPA_DATE: a.npaDate || '',
      ORIGINAL_OUTSTANDING: a.originalOutstandingAmount ?? '',
      OUTSTANDING_BALANCE: a.outstandingAmount ?? '',
      OVERDUE_AMOUNT: a.overdueAmount ?? '',
      TOTAL_RECOVERED: a.totalRecovered ?? 0,
      ACCOUNT_STATUS: a.accountStatus || '',
      OTS_STATUS: a.otsStatus || '',
      FINAL_OTS_AMOUNT: a.finalOTSAmount ?? '',
      ASSIGNED_AGENT_ID: a.assignedAgentId || '',
      ASSIGNED_AGENT_NAME: a.assignedAgentName || '',
      CLOSURE_TYPE: a.closureType || '',
      CLOSURE_DATE: a.closureDate || '',
    })),
    '01_Accounts_With_Activity'
  );

  add(
    mine(recoveries).map((r: any) => ({
      RECOVERY_ID: r.recoveryId || r.id,
      ACCOUNT_ID: r.accountId,
      CUSTOMER_NAME: r.customerName || nameOf(r.accountId),
      BRANCH_NAME: branchOf(r.accountId),
      AMOUNT_COLLECTED: r.amount,
      EXCESS_CHARGES: r.excessChargesAmount ?? 0,
      RECOVERY_DATE: r.recoveryDate,
      PAYMENT_MODE: r.paymentMode || '',
      RECEIPT_NUMBER: r.receiptNumber || '',
      TRANSACTION_REF: r.referenceNumber || '',
      AGENT_ID: r.agentId || '',
      AGENT_NAME: r.agentName || '',
      COMMISSION_PERCENT: r.commissionPercentage ?? '',
      COMMISSION_AMOUNT: r.commissionAmount ?? '',
      IS_REVERSED: r.isReversed ? 'YES' : 'NO',
      REMARKS: r.remarks || '',
    })),
    '02_Collections'
  );

  add(
    mine(commissions).map((c: any) => ({
      COMMISSION_ID: c.id,
      RECOVERY_ID: c.recoveryId || '',
      ACCOUNT_ID: c.accountId || '',
      CUSTOMER_NAME: nameOf(c.accountId),
      BRANCH_NAME: branchOf(c.accountId),
      AGENT_ID: c.agentId || '',
      AGENT_NAME: c.agentName || '',
      RECOVERY_AMOUNT: c.recoveryAmount ?? '',
      COMMISSION_RATE: c.commissionPercentage ?? '',
      COMMISSION_AMOUNT: c.commissionAmount ?? '',
      STATUS: c.status || c.commissionPaidStatus || '',
      MONTH: c.month || String(c.recoveryDate || '').slice(0, 7),
    })),
    '03_Commissions'
  );

  add(
    mine(followups).map((f: any) => ({
      FOLLOWUP_ID: f.id,
      ACCOUNT_ID: f.accountId,
      CUSTOMER_NAME: f.customerName || nameOf(f.accountId),
      BRANCH_NAME: branchOf(f.accountId),
      DATE: f.followUpDate || f.date || '',
      TIME: f.time || '',
      MODE: f.contactMode || f.mode || '',
      STATUS: f.status || '',
      AGENT_NAME: f.agentName || '',
      NEXT_ACTION_DATE: f.nextFollowUpDate || '',
      REMARKS: f.remarks || f.notes || '',
    })),
    '04_Followups'
  );

  add(
    mine(ptps).map((p: any) => ({
      PTP_ID: p.id,
      ACCOUNT_ID: p.accountId,
      CUSTOMER_NAME: p.customerName || nameOf(p.accountId),
      BRANCH_NAME: branchOf(p.accountId),
      PROMISED_DATE: p.ptpDate || '',
      PROMISED_AMOUNT: p.ptpAmount ?? '',
      STATUS: p.status || '',
      AGENT_NAME: p.agentName || '',
      CREATED: p.createdDate || p.createdAt || '',
      REMARKS: p.remarks || '',
    })),
    '05_PTP_Commitments'
  );

  add(
    mine(visits).map((v: any) => ({
      VISIT_ID: v.id,
      ACCOUNT_ID: v.accountId,
      CUSTOMER_NAME: v.customerName || nameOf(v.accountId),
      BRANCH_NAME: branchOf(v.accountId),
      DATE: v.visitDate || v.date || '',
      TIME: v.time || '',
      OUTCOME: v.outcome || v.status || '',
      AGENT_NAME: v.agentName || '',
      LATITUDE: v.latitude ?? '',
      LONGITUDE: v.longitude ?? '',
      ADDRESS: v.address || '',
      REMARKS: v.remarks || v.notes || '',
    })),
    '06_Field_Visits'
  );

  // The notes live on the account, so they are flattened into rows here --
  // otherwise the agent's own written record of the case is not in the backup
  // at all.
  const noteRows: any[] = [];
  kept.forEach((a: any) => {
    (a.agentNotesHistory || []).forEach((n: any) => {
      noteRows.push({
        ACCOUNT_ID: a.accountId,
        CUSTOMER_NAME: a.customerName,
        BRANCH_NAME: a.branch || '',
        DATE: n.date || '',
        TIME: n.time || '',
        CATEGORY: n.category || '',
        AGENT_NAME: n.agentName || '',
        NOTE: n.note || '',
      });
    });
  });
  add(noteRows, '07_Agent_Remarks');

  const otsRows: any[] = [];
  kept.forEach((a: any) => {
    (a.otsHistory || []).forEach((h: any) => {
      otsRows.push({
        ACCOUNT_ID: a.accountId,
        CUSTOMER_NAME: a.customerName,
        BRANCH_NAME: a.branch || '',
        TIMESTAMP: h.timestamp || '',
        ACTION: h.action || '',
        ORIGINAL_OUTSTANDING: h.originalOutstandingAmount ?? '',
        SYSTEM_OTS: h.systemCalculatedOTSAmount ?? '',
        FINAL_OTS: h.finalOTSAmount ?? '',
        CONCESSION: h.actualConcessionAmount ?? '',
        BY_USER: h.userName || '',
        REMARKS: h.remarks || '',
      });
    });
  });
  // Worth its own sheet: this is the only surviving record of what an account
  // was worth BEFORE a settlement was applied to it.
  add(otsRows, '08_OTS_History');

  add(
    mine(allocationHistories).map((al: any) => ({
      ALLOCATION_ID: al.id,
      ACCOUNT_ID: al.accountId,
      CUSTOMER_NAME: nameOf(al.accountId),
      BRANCH_NAME: branchOf(al.accountId),
      DATE: al.allocationDate || al.date || '',
      FROM_AGENT: al.previousAgentName || '',
      TO_AGENT: al.agentName || al.newAgentName || '',
      BY_USER: al.allocatedByName || '',
      REASON: al.reason || '',
    })),
    '09_Allocation_History'
  );

  add(
    (users || []).map((u: any) => ({
      USER_ID: u.id,
      AGENT_ID: u.agentId || '',
      NAME: u.name || '',
      LOGIN_ID: u.username || '',
      ROLE: u.role || '',
      MOBILE: u.mobile || '',
      EMAIL: u.email || '',
      BANK: (u.banks && u.banks.length ? u.banks.join(' | ') : u.bank) || '',
      ZONE: u.zone || '',
      BRANCH: u.branch || '',
      COMMISSION_PERCENT: u.commissionPercent ?? '',
      SARFAESI_PERCENT: u.sarfaesiCommissionPercent ?? '',
      MONTHLY_TARGET: u.monthlyTarget ?? '',
      STATUS: u.status || '',
    })),
    '10_Users_and_Agents'
  );

  // The structure the rest of the file refers to. Missing from both earlier
  // exports, which made a restore guesswork.
  add(
    (banks || []).map((b: any) => ({
      BANK_ID: b.id || '',
      BANK_NAME: b.name || '',
      SHORT_NAME: b.shortName || '',
      STATUS: b.status || '',
    })),
    '11_Banks'
  );
  add(
    (zones || []).map((z: any) => ({
      ZONE_ID: z.id || '',
      ZONE_NAME: z.name || '',
      BANK_NAME: z.bankName || '',
      STATUS: z.status || '',
    })),
    '12_Zones'
  );
  add(
    (branches || []).map((br: any) => ({
      BRANCH_ID: br.id || '',
      BRANCH_NAME: br.name || '',
      BRANCH_CODE: br.code || br.branchCode || '',
      ZONE_NAME: br.zoneName || '',
      BANK_NAME: br.bankName || '',
      CITY: br.city || '',
      STATUS: br.status || '',
    })),
    '13_Branches'
  );

  add(
    (activityLogs || []).map((l: any) => ({
      LOG_ID: l.id,
      TIMESTAMP: l.timestamp || '',
      USER_NAME: l.userName || '',
      USER_ROLE: l.userRole || '',
      ACTION: l.action || '',
      ACCOUNT_ID: l.accountId || '',
      DETAILS: l.details || l.description || '',
    })),
    '14_Activity_Audit_Trail'
  );

  const skipped = (accounts || []).length - kept.length;

  // A sheet saying what this file is and what was left out, so the file can
  // be understood a year from now without anybody remembering this screen.
  add(
    [
      { FIELD: 'Backup taken', VALUE: new Date().toLocaleString('en-IN') },
      { FIELD: 'Accounts loaded', VALUE: (accounts || []).length },
      { FIELD: 'Accounts with activity (included)', VALUE: kept.length },
      { FIELD: 'Accounts with no activity (not included)', VALUE: skipped },
      { FIELD: 'Collections', VALUE: mine(recoveries).length },
      { FIELD: 'Commissions', VALUE: mine(commissions).length },
      { FIELD: 'Follow-ups', VALUE: mine(followups).length },
      { FIELD: 'PTP commitments', VALUE: mine(ptps).length },
      { FIELD: 'Field visits', VALUE: mine(visits).length },
      { FIELD: 'Agent remarks', VALUE: noteRows.length },
      { FIELD: 'OTS history entries', VALUE: otsRows.length },
      { FIELD: 'Users', VALUE: (users || []).length },
      { FIELD: 'Activity log entries', VALUE: (activityLogs || []).length },
      {
        FIELD: 'Note',
        VALUE:
          'Accounts with no recorded activity are not included -- they can be ' +
          're-imported from the bank sheet they came from. Everything that was ' +
          'DONE in ScaleSupport is here.',
      },
      {
        FIELD: 'Scope',
        VALUE:
          'This covers the accounts loaded in the browser when the backup was ' +
          'taken. Load all accounts first for a backup of the whole book.',
      },
    ],
    '00_Backup_Summary'
  );

  const finalName = filename || `ScaleSupport_Full_Backup_${todayLocal()}.xlsx`;
  XLSX.writeFile(workbook, finalName);

  return { accountsIncluded: kept.length, accountsSkipped: skipped, filename: finalName };
}

